<template>
  <el-dialog
    :title="$t('배달 연동 상태')"
    :visible="visible"
    width="920px"
    :close-on-click-modal="false"
    @close="close">
    <div v-loading="loading" v-if="detail">
      <el-descriptions :column="3" border size="small">
        <el-descriptions-item :label="$t('메뉴명')">{{ detail.smenuNm }}</el-descriptions-item>
        <el-descriptions-item :label="$t('축')">
          <el-tag size="mini" :type="detail.axis === 'ORIGINAL' ? '' : 'info'">
            {{ detail.axis === 'ORIGINAL' ? $t('원본') : $t('사본') }}
          </el-tag>
          <el-button
            v-if="detail.axis !== 'ORIGINAL'"
            type="text"
            size="mini"
            @click="$emit('open-menu', detail.originalSmenuCd)">
            {{ $t('원본 메뉴 보기') }}
          </el-button>
        </el-descriptions-item>
        <el-descriptions-item :label="$t('연동된 플랫폼')">
          <span v-if="detail.coverage && detail.coverage.length">
            {{ detail.coverage.map(platformName).join(', ') }}
          </span>
          <span v-else>-</span>
        </el-descriptions-item>
      </el-descriptions>

      <el-alert
        v-if="detail.siblingDeliveryOverlap && detail.siblingDeliveryOverlap.length"
        class="dm-detail__alert"
        type="info"
        :closable="false"
        show-icon
        :title="siblingText"></el-alert>

      <div class="dm-detail__section">
        <div class="dm-detail__label">{{ $t('플랫폼별 상태') }}</div>
        <el-table :data="detail.platforms" size="mini" border>
          <el-table-column :label="$t('플랫폼')" width="110">
            <template slot-scope="s">{{ platformName(s.row.platform) }}</template>
          </el-table-column>
          <!-- `이름(번호)` 한 줄 — 조립 규칙은 utils/deliveryMenuSync 한 곳 (§16.179) -->
          <el-table-column :label="$t('가게')" width="170" show-overflow-tooltip>
            <template slot-scope="s">{{ shop(s.row) }}</template>
          </el-table-column>
          <el-table-column :label="$t('상태')" min-width="220">
            <template slot-scope="s">
              <delivery-menu-chip :status="s.row" :clickable="false" />
              <div v-if="chipOf(s.row).note" class="dm-detail__note">{{ chipOf(s.row).note }}</div>
              <div v-if="shopNote(s.row)" class="dm-detail__note">{{ shopNote(s.row) }}</div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('플랫폼 값')" min-width="180">
            <template slot-scope="s">
              <el-tag v-if="badgeOf(s.row)" :type="badgeOf(s.row).tagType" size="mini">
                {{ badgeOf(s.row).text }}
              </el-tag>
              <span v-else>-</span>
              <div v-if="driftFieldText(s.row)" class="dm-detail__note">{{ driftFieldText(s.row) }}</div>
              <div v-if="unsupportedText(s.row)" class="dm-detail__note dm-detail__note--weak">
                {{ unsupportedText(s.row) }}
              </div>
            </template>
          </el-table-column>
          <el-table-column :label="$t('동기화')" width="90" align="center">
            <template slot-scope="s">
              <el-switch
                :value="s.row.syncEnabled"
                :disabled="!canPolicy || s.row.linkState == null"
                @change="togglePolicy(s.row, $event)"></el-switch>
            </template>
          </el-table-column>
          <el-table-column :label="$t('조치')" width="150" align="center">
            <template slot-scope="s">
              <el-button
                v-if="chipOf(s.row).resolve === 'BIND'"
                type="text"
                size="mini"
                :disabled="!canBind"
                @click="$emit('bind', s.row)">
                {{ $t('메뉴 연결') }}
              </el-button>
              <el-button
                v-else-if="chipOf(s.row).resolve === 'ORPHAN'"
                type="text"
                size="mini"
                @click="$emit('go-orphan', s.row)">
                {{ $t('연결 화면으로') }}
              </el-button>
              <el-button
                v-else-if="chipOf(s.row).resolve === 'DRIFT'"
                type="text"
                size="mini"
                @click="$emit('go-drift', s.row)">
                {{ $t('차이 해결로') }}
              </el-button>
              <span v-else>-</span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="dm-detail__section" v-if="detail.copies && detail.copies.length">
        <div class="dm-detail__label">{{ $t('형제 매장 사본별 상태') }}</div>
        <el-table :data="detail.copies" size="mini" border>
          <el-table-column :label="$t('매장')" width="100" align="center">
            <template slot-scope="s">{{ s.row.storeCd }}</template>
          </el-table-column>
          <el-table-column :label="$t('메뉴명')" prop="smenuNm" min-width="140" show-overflow-tooltip></el-table-column>
          <el-table-column :label="$t('상태')" min-width="320">
            <template slot-scope="s">
              <delivery-menu-chip
                v-for="p in s.row.platforms"
                :key="p.platform + '-' + (p.linkId || 'none')"
                :status="p"
                :clickable="false" />
            </template>
          </el-table-column>
          <el-table-column width="90" align="center">
            <template slot-scope="s">
              <el-button type="text" size="mini" @click="$emit('open-menu', s.row.smenuCd)">
                {{ $t('열기') }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="dm-detail__section">
        <div class="dm-detail__label">{{ $t('반영 이력') }}</div>
        <el-table :data="detail.history" size="mini" border :empty-text="$t('이력 없음')" max-height="260">
          <el-table-column :label="$t('시각')" width="150">
            <template slot-scope="s">{{ formatDate(s.row.finishedAt || s.row.createdAt) }}</template>
          </el-table-column>
          <el-table-column :label="$t('플랫폼')" width="90">
            <template slot-scope="s">{{ platformName(s.row.platform) }}</template>
          </el-table-column>
          <el-table-column :label="$t('상태')" width="150">
            <template slot-scope="s">
              <el-tag :type="itemChip(s.row).tagType" size="mini">{{ itemChip(s.row).text }}</el-tag>
            </template>
          </el-table-column>
          <el-table-column :label="$t('대상')" min-width="150" show-overflow-tooltip>
            <template slot-scope="s">
              {{ s.row.executionScopeDesc || shopsText(s.row.shops) || '-' }}
            </template>
          </el-table-column>
          <el-table-column :label="$t('설명')" min-width="220" show-overflow-tooltip>
            <!-- 서버 문장이 없으면 칩의 정적 안내를 보인다 — 칩 표면과 같은 규칙이다 -->
            <!-- 「서버 문장이 이긴다」는 `deliveryMenuSync.decorate` 가 소유한다 — 여기 사본을 두면
                 우선순위를 바꿀 때 두 곳이 갈린다. `itemChip` 은 항상 LINKED + lastItem 을 넘긴다. -->
            <template slot-scope="s">{{ itemChip(s.row).note || '-' }}</template>
          </el-table-column>
        </el-table>
      </div>

      <!--
        **옵션 축이 이 표에서 어떻게 읽히는지를 말한다** (§16.198).

        이 다이얼로그는 메뉴 하나의 **플랫폼별 반영 상태를 읽는 자리**다. 옵션 축은 이제 배민·쿠팡이츠
        에서 나가지만, **이 표는 옵션이 나갔는지를 따로 말하지 않는다** — 두 플랫폼 모두 옵션의 실행
        범위가 이름·가격과 같아 **한 item 에 함께 실리므로** chip 하나가 셋을 함께 말한다. 그래서
        서버가 옵션 축만 접었을 때 화면에 남는 것은 둘 중 하나다: 그 편집이 옵션뿐이었으면
        `해당 변경 미지원` 한 줄(사유는 서버가 담지 않는다), 이름·가격을 같이 고쳤으면 **아무 표시도
        없이** 이름·가격만 나간 `반영 완료`. 뒤쪽이 위험한 쪽이라 두 사실을 화면이 먼저 말한다.

        ① **경계** — 나가는 것은 연결뿐이다. 이 문장이 없으면 "이제 옵션이 반영된다" 로 읽혀 사장님이
           오더엔에서 옵션 이름을 고쳐 두고 기다린다.
        ② **선행 조건** — 재료(관측)가 없는 메뉴에서는 옵션 축만 접힌다. 개통 직후에는 그쪽이
           대부분이라(리얼 514 링크 중 494) 조건부가 아니라 **무조건** 말한다. 어느 링크에 재료가
           있는지는 §9.2 응답에 아직 없어 화면이 가려 띄울 수 없다.

        둘 다 `해당 변경 미지원` 이 사후에 도착하는 자리라, [PICKUP_PUSH_CONDITION] 과 같은 규칙으로
        **먼저** 말한다. 문구는 `utils/deliveryMenuNotes` 한 곳이 소유한다 — 화면마다 다시 쓰면 그
        사본이 곧 사실의 두 번째 출처가 된다.
      -->
      <el-alert
        v-for="(note, i) in optionScopeNotes"
        :key="'opt-scope-' + i"
        class="dm-detail__alert"
        type="info"
        :closable="false"
        show-icon
        :title="note"></el-alert>
      <el-alert
        class="dm-detail__alert"
        type="warning"
        :closable="false"
        show-icon
        :title="optionPrerequisiteNote"></el-alert>
      <!--
        ★ **여러 배달앱에 올라간 메뉴만 읽는 줄** (§16.201) — 위 둘과 달리 조건부다.
        인바운드 `align` 이 메뉴 단위라 마지막 가져오기의 옵션 구성만 오더엔에 남고 나머지
        배달앱의 반영이 접힌다. 근본 수정은 「이 링크에 무엇이 있어야 하는가」를 적는 축이
        필요해 별도 회차이며, 그때까지 이 줄이 사실을 말한다.
      -->
      <el-alert
        v-if="optionMultiPlatformNote"
        class="dm-detail__alert"
        type="warning"
        :closable="false"
        show-icon
        :title="optionMultiPlatformNote"></el-alert>
      <!--
        ★ **지목 충돌은 오류로 드러낸다** (§16.199). 이름 키가 새로 만드는 유일한 실패 모드이고,
        위의 둘과 달리 **조건부**다 — 관측에 같은 이름이 둘 있을 때만 뜬다. 조건을 붙일 수 있는
        이유는 그 사실이 §9.2 응답에 있기 때문이다(`platforms[].optionAxis.observedNames`).

        서버가 사유를 내리지 않는 것이 이 자리의 규율이다 — 동등 판정의 소유자는 룰 하나여야 하고,
        서버가 자기 동치로 「충돌 없음」이라 하면 그 순간 같은 술어가 두 벌이 된다. 화면은 관측에서
        **파생**해 말한다(저장하는 것은 관측 하나뿐이라 §2 원칙 3 그대로다).
      -->
      <el-alert
        v-for="note in optionAmbiguousNotes"
        :key="note.key"
        class="dm-detail__alert"
        type="error"
        :closable="false"
        show-icon
        :title="note.text"></el-alert>

      <!--
        ★★ **오더엔 쪽 동명 그룹** (규약 §16.228). 위 문단과 **같은 술어·다른 목록**이고 해소
        방향이 반대다 — 위는 「배달앱에서 이름을 바꾸세요」, 여기는 「오더엔에서 바꾸세요」.
        서버는 그 상황에서 축을 접지만(작업을 만들기 전에) 그 사유를 읽는 코드가 없었고,
        그래서 사장님에게는 **아무 일도 일어나지 않는 것처럼** 보였다.
      -->
      <el-alert
        v-for="note in optionOrdernAmbiguousNotes"
        :key="note.key"
        class="dm-detail__alert"
        type="error"
        :closable="false"
        show-icon
        :title="note.text"></el-alert>

      <!--
        ★★ **내 편집이 남의 매장에도 나간다** (`CATALOG-SHARED`). 배민 카탈로그가 owner 소유라
        서로 다른 오더엔 매장이 같은 메뉴를 공유할 수 있고, 그때 한쪽의 편집이 다른 쪽에 그대로
        나간다(dev 실측: store 13 → store 792). 서버 §8 은 이것을 막지 않으며 막지 않는 것이
        맞다 — 남은 것이 침묵이라 이 줄이 그 침묵을 없앤다. 경고이지 오류가 아니다.
      -->
      <el-alert
        v-for="note in catalogSharedNotes"
        :key="note.key"
        class="dm-detail__alert"
        type="warning"
        :closable="false"
        show-icon
        :title="note.text"></el-alert>

      <el-alert
        class="dm-detail__alert"
        type="info"
        :closable="false"
        :title="$t('반영에 실패한 항목은 메뉴를 다시 저장하면 새 반영 요청이 만들어집니다.')"></el-alert>
    </div>

    <div slot="footer">
      <el-button v-if="detail && mappingAvailable" size="small" @click="openOptionMapping">{{
        $t('옵션 연결')
      }}</el-button>
      <el-button size="small" @click="reload">{{ $t('새로고침') }}</el-button>
      <el-button size="small" type="primary" @click="close">{{ $t('닫기') }}</el-button>
    </div>
  </el-dialog>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { util } from '@/mixins/utilMixin'
import DeliveryMenuChip from './DeliveryMenuChip.vue'
import {
  DELIVERY_MENU_ACTIONS,
  DELIVERY_MENU_API,
  deliveryFieldLabel,
  deliveryMenuChip,
  deliveryMenuErrorMessage,
  deliveryMenuPlatformName,
  deliveryMenuTimeText,
  driftBadge,
  shopsNote,
  shopText,
} from '@/utils/deliveryMenuSync'
import {
  catalogSharedNote,
  duplicateObservedOptionNames,
  hasAnyOptionAxisOpen,
  OPTION_MULTI_PLATFORM_LAST_IMPORT_WINS,
  OPTION_OUTBOUND_NEEDS_IMPORT,
  optionOrdernAmbiguousNote,
  optionOutboundAmbiguousNote,
  optionOutboundNotes,
} from '@/utils/deliveryMenuNotes'
import { checkAuthActionAccess } from '@/utils/checkAuthActionAccess'

/**
 * ① 메뉴 상세 — `GET /status/{smenuCd}`.
 *
 * **`axis` 를 쿼리로 보내지 않는다** (§16.44). 축은 서버가 `master_smenu_cd` 에서 파생하고
 * 응답의 `axis` 가 그 값이다. 원본 상세면 `copies[]` 가 함께 온다.
 *
 * 조회가 곧 지연 재계획이다 (§6.1) — 열려 있는 동안에만 조회하고 주기 폴링은 하지 않는다.
 */
export default {
  name: 'DeliveryMenuDetailDialog',
  mixins: [mainVueMixin],
  components: { DeliveryMenuChip },

  props: {
    visible: { type: Boolean, default: false },
    smenuCd: { type: Number, default: null },
    /** 상세가 속한 매장 — 정책 변경(`PUT /policy`)의 축 */
    storeCd: { type: Number, default: null },
    mappingAvailable: { type: Boolean, default: true },
  },

  data() {
    return { detail: null, loading: false }
  },

  computed: {
    canPolicy() {
      return checkAuthActionAccess(DELIVERY_MENU_ACTIONS.POLICY_UP)
    },
    canBind() {
      return checkAuthActionAccess(DELIVERY_MENU_ACTIONS.BIND)
    },
    siblingText() {
      const names = (this.detail.siblingDeliveryOverlap || []).map((s) => s.storeNm || s.storeCd).join(', ')
      return this.$t('이 메뉴는 형제 매장에도 배달 채널로 올라가 있습니다') + ': ' + names
    },
    /**
     * 옵션 축이 미는 것과 밀지 않는 것 — **capability 에서 파생한다** (§16.214).
     *
     * 종전에는 상수 하나였고 그것이 capability 표의 두 번째 사본이라 표가 바뀌어도 안 바뀌었다.
     * 이제 서버가 내린 `fieldSupport[]` 를 **이 메뉴가 실제로 올라간 플랫폼**으로 좁혀 문장을 만든다 —
     * 올라가 있지 않은 플랫폼의 지원 폭은 이 메뉴에 대해 존재하지 않는 사실이다(§16.7).
     */
    optionScopeNotes() {
      const platforms = (this.detail.platforms || []).filter((p) => p.linkId != null).map((p) => p.platform)
      return optionOutboundNotes(this.detail.fieldSupport, platforms)
    },
    /** 그 반영의 선행 조건(「메뉴 가져오기」 1회) — 같은 정본 */
    optionPrerequisiteNote() {
      return OPTION_OUTBOUND_NEEDS_IMPORT
    },
    /**
     * ★ **여러 배달앱에 올라간 메뉴에서는 마지막 가져오기가 옵션의 주인이다** — 조건부 (§16.201).
     *
     * 판정 축은 이 응답의 `platforms[]` 길이다 — 그 배열이 곧 「이 메뉴가 올라간 배달앱」이라
     * 새 조회가 없다. 한 곳뿐이면 **존재하지 않는 사실**이므로 띄우지 않는다(§16.7 의 규칙).
     * 문구의 정본은 `utils/deliveryMenuNotes` 이고 메뉴 목록 화면과 **같은 문장**을 쓴다 —
     * 두 화면이 다른 말을 하면 경계를 그은 문장에서 한쪽이 곧 거짓이다.
     */
    optionMultiPlatformNote() {
      const platforms = (this.detail && this.detail.platforms) || []
      return platforms.length > 1 ? OPTION_MULTI_PLATFORM_LAST_IMPORT_WINS : null
    },
    /**
     * ★ **지목 불가(같은 이름 둘)를 플랫폼별로 말한다** (§16.199).
     *
     * 술어도 문구도 `utils/deliveryMenuNotes` 가 소유한다 — 이 화면이 중복 판정을 직접 적으면
     * 그 사본이 룰의 판정과 갈리고, 갈리는 순간 화면이 "괜찮다"고 말한 메뉴에서 반영이 멎는다.
     *
     * 같은 매장이 여러 배달앱에 올라가 있으면 플랫폼마다 사정이 다르므로 줄도 플랫폼마다다.
     */
    /**
     * ★★ **배민 카탈로그를 다른 매장과 공유한다** (`CATALOG-SHARED`).
     *
     * 사실(`sharedStoreCount`)은 서버가 §11.2 결정과 **같은 조회**로 세고, 문장은 여기서 만든다 —
     * §16.214 가 세운 「사실은 서버 · 문장은 화면」 규칙 그대로다. 남의 매장 코드는 응답에 없다.
     */
    catalogSharedNotes() {
      const platforms = (this.detail && this.detail.platforms) || []
      return platforms
        .map((row) => ({ row, text: catalogSharedNote(row.sharedStoreCount) }))
        .filter((entry) => entry.text !== '')
        .map((entry) => ({
          key: 'shared:' + entry.row.platform + ':' + (entry.row.linkId || ''),
          // 플랫폼 접두어를 붙이지 않는다 — 문장이 이미 「배민에서」로 시작해 「배달의민족 — 이
          // 메뉴는 배민에서…」로 두 번 말하게 된다 (§16.223). 이 축은 배민에서만 0 이 아니다.
          text: entry.text,
        }))
    },
    /**
     * ★★ **원인이 메뉴 축이므로 줄도 메뉴 축이다** (§16.229 정정).
     *
     * 초판은 `platforms` 를 그대로 돌아 **링크마다 한 줄**을 냈다. 그런데 재료인 `ordernNames` 는
     * 서버가 그 메뉴의 오더엔 옵션그룹에서 채우므로 **모든 행에 같은 값**이고, `platforms` 는
     * 플랫폼당이 아니라 **링크당** 한 행이다(한 메뉴에 링크 3개가 흔하다) — 사장님이 글자 하나
     * 다르지 않은 빨간 배너를 셋 봤다.
     *
     * 그리고 **옵션 축이 하나도 열려 있지 않으면 말하지 않는다** — §16.214 가 세운 capability
     * 파생 규칙이다. 아무것도 동기화되지 않는 축에 조치를 요구하면 안 된다.
     */
    optionOrdernAmbiguousNotes() {
      if (!hasAnyOptionAxisOpen(this.detail && this.detail.fieldSupport)) return []
      const linked = ((this.detail && this.detail.platforms) || []).filter((row) => row.linkId != null)
      const text = linked.length === 0 ? '' : optionOrdernAmbiguousNote(linked[0].optionAxis)
      // 플랫폼 접두어를 붙이지 않는다 — 원인이 오더엔 쪽이라 플랫폼마다 같은 문장이 된다.
      return text === '' ? [] : [{ key: 'ordern-dup', text }]
    },
    optionAmbiguousNotes() {
      const platforms = (this.detail && this.detail.platforms) || []
      return platforms
        .map((row) => ({ row, names: duplicateObservedOptionNames(row.optionAxis) }))
        .filter((entry) => entry.names.length > 0)
        .map((entry) => ({
          key: entry.row.platform + ':' + (entry.row.linkId || ''),
          text: deliveryMenuPlatformName(entry.row.platform) + ' — ' + optionOutboundAmbiguousNote(entry.names),
        }))
    },
  },

  watch: {
    visible(open) {
      if (open) this.reload()
      else this.detail = null
    },
    smenuCd() {
      if (this.visible) this.reload()
    },
  },

  methods: {
    openOptionMapping() {
      if (!this.detail || !this.mappingAvailable) return
      const query = { storeCd: this.storeCd || this.detail.storeCd, smenuCd: this.detail.smenuCd }
      this.close()
      if (this.$listeners['open-option-mapping']) {
        this.$emit('open-option-mapping', query)
        return
      }
      this.$router.push({ path: '/v/business/delivery-menu/option-mappings', query })
    },
    platformName: deliveryMenuPlatformName,
    chipOf: deliveryMenuChip,
    /** 샵 표기 규칙은 utils/deliveryMenuSync 한 곳이 소유한다 (§16.179) */
    shop: shopText,
    /** 이력 행의 대상 샵 — 부기와 같은 표기를 쓴다(번호만 나열하지 않는다, §16.179) */
    shopsText(shops) {
      return (shops || []).map(shopText).join(', ')
    },
    badgeOf(row) {
      return driftBadge(row.drift)
    },
    shopNote(row) {
      return row.lastItem ? shopsNote(row.lastItem.shops) : null
    },
    itemChip(item) {
      // 이력 행은 링크가 아니라 item 이다 — chip 규칙을 재사용하도록 최소 형태로 감싼다
      return deliveryMenuChip({ linkState: 'LINKED', lastItem: item, syncEnabled: true })
    },
    driftFieldText(row) {
      const drift = row.drift
      if (!drift || !drift.fields || !drift.fields.length) return null
      return this.$t('차단 항목') + ': ' + drift.fields.map(deliveryFieldLabel).join(', ')
    },
    unsupportedText(row) {
      const drift = row.drift
      if (!drift || !drift.unsupportedDiffFields || !drift.unsupportedDiffFields.length) return null
      return this.$t('표시만') + ': ' + drift.unsupportedDiffFields.map(deliveryFieldLabel).join(', ')
    },
    formatDate(value) {
      return deliveryMenuTimeText(value, util.getDate)
    },

    async reload() {
      if (this.smenuCd == null) return
      this.loading = true
      try {
        const res = await this.$axios.get(`${DELIVERY_MENU_API}/status/${this.smenuCd}`, {
          params: { history: 10 },
        })
        this.detail = res.data
      } catch (e) {
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e) })
        this.close()
      } finally {
        this.loading = false
      }
    },

    async togglePolicy(row, enabled) {
      const storeCd = this.storeCd != null ? this.storeCd : this.detail && this.detail.storeCd
      if (storeCd == null) return
      try {
        await this.$axios.put(`${DELIVERY_MENU_API}/policy`, {
          scope: 'MENU',
          storeCd,
          smenuCd: this.detail.smenuCd,
          platform: row.platform,
          enabled,
        })
        this.$message({ type: 'success', message: this.$t('동기화 설정을 저장했습니다.') })
        this.reload()
        this.$emit('changed')
      } catch (e) {
        this.$message({ type: 'error', message: deliveryMenuErrorMessage(e) })
        this.reload()
      }
    },

    close() {
      this.$emit('update:visible', false)
    },
  },
}
</script>

<style scoped>
.dm-detail__section {
  margin-top: 18px;
}
.dm-detail__label {
  font-weight: 600;
  margin-bottom: 8px;
}
.dm-detail__note {
  font-size: 12px;
  color: #909399;
  margin-top: 2px;
}
.dm-detail__note--weak {
  opacity: 0.75;
}
.dm-detail__alert {
  margin-top: 14px;
}
</style>
